import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { makeStyles, useTheme } from 'pencil/styles';
import { Switch, Route, Redirect } from 'circe/routing/provider';
import RequireAuth from 'circe/authorization/components/AuthorizationRoute';
import { AUTHORITIES } from 'domain/fuse/auth/constants';

import Container from 'pencil/Container';
import withData from 'circe/Http/withData';
import { HEADER_HEIGHT } from '../constants';
import ShipmentsRoutes from 'domain/shipment/routes';
import OperationsDashboardRoutes from 'domain/operations/dashboard/routes';
import FinanceRoutes from 'domain/finance/routes';
import ReceiveRoutes from 'domain/shipment/receive/routes';
import ReturnRoutes from 'domain/shipment/return/routes';
import SchedulerRoutes from 'domain/scheduler/routes';
import CarriersRoutes from 'domain/carriers/routes';
import AccountAdminsRoutes from 'domain/accountAdmins/routes';
import DashboardRoutes from 'domain/dashboard/routes';
import BatchRoutes from 'domain/batch/routes';
import OperationsRoutes from 'domain/operations/routes';
import UsersRoutes from 'domain/fuse/auth/users/routes';
import ZoningRoutes from 'domain/addressBook/zone/routes';
import ProducTypesRoutes from 'domain/productType/routes';
import BatchingSettingsRoutes from 'domain/settings/batching/routes';
import ManifestRoutes from 'domain/manifest/routes';
import VehcileRoutes from 'domain/vehicle/routes';

import { loadComponent } from 'domain/layout/utils';
import TripRoutes from 'domain/trip/routes';
import FactoryUploadRoutes from 'domain/shipment/factoryUpload/routes';
const TapContainer = loadComponent(() => {
  return import('domain/tap/container');
});

const authTap = RequireAuth(TapContainer, [AUTHORITIES.ACCOUNT_ADMIN], '/');

import { authorizeContainer as Finance } from 'domain/finance/creditTerms/routes';
import { ROUTES as FINANCE_ROUTES } from 'domain/finance/creditTerms/constants';
import { ROUTES as MY_SHIPMENTS_ROUTES } from 'domain/shipment/mine/constants';
import { ROUTES as DRIVERS_ROUTES } from 'domain/operations/drivers/constants';

const createContainer = loadComponent(() => import('domain/addressBook/components/Create'));
const AddressBookCreateContainer = loadComponent(() =>
  import('domain/addressBook/components/Create'),
);

const SearchContainer = loadComponent(() => import('domain/order'));
const dashboardContainer = loadComponent(() => import('domain/CEO/container'));

const HistoryContainer = loadComponent(() => import('domain/shipment/history/components/History'));

const TrackingListContainer = loadComponent(() =>
  import('domain/tracking/components/TrackingList'),
);
const ResultsContainer = loadComponent(() => import('domain/order/results/container'));
const LoginComponent = loadComponent(() => import('domain/fuse/auth/components/Login'));
const AddressBookQueryComponent = loadComponent(() =>
  import('domain/addressBook/components/Query'),
);
const ExWorkComponent = loadComponent(() =>
  import('domain/shipmentsManagement/exWorkOrders/container'),
);

const ExworkQueryComponent = RequireAuth(ExWorkComponent, [AUTHORITIES.AUTHENTICATED], '/');

const AddressBookEditComponent = loadComponent(() => import('domain/addressBook/components/Edit'));
const ChartsConfigComponent = loadComponent(() =>
  import('domain/CEO/chartsConfig/components/ChartsConfig'),
);
const PrivayPolicyComponent = loadComponent(() =>
  import('domain/fuse/auth/components/PrivacyPolicy'),
);
const SignupComponent = loadComponent(() => import('domain/fuse/auth/components/Signup'));
const ConfirmSignupComponent = loadComponent(() =>
  import('domain/fuse/auth/components/ConfirmSignup'),
);
const ForgotPasswordContainer = loadComponent(() =>
  import('domain/fuse/auth/components/ForgotPassword'),
);
const ResetPasswordContainer = loadComponent(() =>
  import('domain/fuse/auth/components/ResetPassword'),
);
const AddressBookUpdateContainer = loadComponent(() =>
  import('domain/addressBook/components/Edit'),
);
const Checkout = loadComponent(() => import('domain/fuse/payments/components/Checkout'));

const searchComp = RequireAuth(SearchContainer, [AUTHORITIES.AUTHENTICATED], '/');

const AuthorizedHistoryComponent = RequireAuth(HistoryContainer, [AUTHORITIES.AUTHENTICATED], '/');

const DashboardAuthed = RequireAuth(dashboardContainer, [AUTHORITIES.ACCOUNT_ADMIN], '/');

const AuthorizedAddressBookQueryComponent = RequireAuth(
  AddressBookQueryComponent,
  [
    AUTHORITIES.ACCOUNT_ADMIN,
    AUTHORITIES.DOMAIN_ADMIN,
    AUTHORITIES.OPERATOR,
    AUTHORITIES.SHIPPER,
    AUTHORITIES.DISPATCHER,
  ],
  '/',
);
const AuthorizedAddressBookUpdateContainer = RequireAuth(
  AddressBookUpdateContainer,
  [AUTHORITIES.AUTHENTICATED],
  '/',
);
const AuthorizedAddressBookCreateContainer = RequireAuth(
  AddressBookCreateContainer,
  [
    AUTHORITIES.ACCOUNT_ADMIN,
    AUTHORITIES.DOMAIN_ADMIN,
    AUTHORITIES.OPERATOR,
    AUTHORITIES.SHIPPER,
    AUTHORITIES.DISPATCHER,
  ],
  '/',
);

const orderResultsContainer = RequireAuth(ResultsContainer, [AUTHORITIES.AUTHENTICATED], '/');
const driverEff = RequireAuth(
  TrackingListContainer,
  [AUTHORITIES.AUTHENTICATED, AUTHORITIES.OPERATOR, AUTHORITIES.ACCOUNT_ADMIN],
  '/',
);
const addressBookEditContainer = RequireAuth(
  AddressBookEditComponent,
  [
    AUTHORITIES.ACCOUNT_ADMIN,
    AUTHORITIES.DOMAIN_ADMIN,
    AUTHORITIES.OPERATOR,
    AUTHORITIES.SHIPPER,
    AUTHORITIES.DISPATCHER,
  ],
  '/',
);

const getFirstAccessibleComponent = (authorities) => {
  if (!authorities) {
    return <LoginComponent />;
  }

  if (authorities.indexOf(AUTHORITIES.ACCOUNT_ADMIN) > -1) {
    return <Redirect to={'/masheed/dashboard'} />;
  }

  if (authorities.indexOf(AUTHORITIES.FINANCE_ADMIN) > -1) {
    return <Redirect to={'/finance/payables'} />;
  }

  if (authorities.indexOf(AUTHORITIES.SHIPPER) > -1) {
    return <Redirect to={'/address-book/query'} />;
  }

  if (authorities.indexOf(AUTHORITIES.FLEET_ADMIN) > -1) {
    return <Redirect to={DRIVERS_ROUTES.ROOT} />;
  }

  if (authorities.indexOf(AUTHORITIES.CALL_CENTER_ASSOCIATE) > -1) {
    return <Redirect to="/shipment/track" />;
  }
  if (authorities.indexOf(AUTHORITIES.DISPATCHER) > -1) {
    return <Redirect to="/orders" />;
  }
  return <Redirect to={'/'} />;
};

const renderRoutes = (isLogged, authorities) => {
  const SHARED_UI_ROUTES = [
    <Route path="/results" key="/results" component={orderResultsContainer} />,
    <Route path="/tracking" key="/tracking" component={driverEff} />,
    <Route
      path="/address-book/new"
      key="/address-book/new"
      component={AuthorizedAddressBookCreateContainer}
    />,
    <Route path="/tap" key="/tap" component={authTap} />,
    <Route
      path="/address-book/query"
      key="/address-book/query"
      component={AuthorizedAddressBookQueryComponent}
    />,
    <Route path="/exworks" key="/exworks" component={ExworkQueryComponent} />,
    <Route
      path="/address-book/update/:code"
      key="/address-book/update/:code"
      component={AuthorizedAddressBookUpdateContainer}
    />,
    <Route
      path="/address-book/edit/:id"
      key="/address-book/edit/:id"
      component={addressBookEditContainer}
    />,
    <Route
      exact
      key="root"
      path="/"
      render={() => (isLogged ? getFirstAccessibleComponent(authorities) : <LoginComponent />)}
    />,
    <Route
      path="/payment-success"
      key="/payment-success"
      component={RequireAuth(Checkout, [AUTHORITIES.AUTHENTICATED], '/')}
    />,
    <Route
      exact
      path="/forgot-password"
      key="/forgot-password"
      component={ForgotPasswordContainer}
    />,
    <Route exact path="/reset-password" key="/reset-password" component={ResetPasswordContainer} />,
    <Route path="/search" key="/search" component={searchComp} />,

    <Route
      exact
      path="/login"
      key="/login"
      render={() => (isLogged ? <Redirect to="/" /> : <LoginComponent />)}
    />,
    <Route exact path="/privacy-policy" key="/privacy-policy" component={PrivayPolicyComponent} />,
  ];

  const PULSE_UI_ROUTES = [
    <Route
      path="/witchcraft/tables/create"
      key="/witchcraft/tables/create"
      component={RequireAuth(createContainer, [AUTHORITIES.CEO, AUTHORITIES.ACCOUNT_AMDIN], '/')}
    />,
    <Route path="/dashboard" key="/dashboard" component={authTap} />,

    <Route
      path="/charts-config"
      key="/charts-config"
      redirectTo="/login"
      component={RequireAuth(
        ChartsConfigComponent,
        [AUTHORITIES.CEO, AUTHORITIES.ACCOUNT_AMDIN],
        '/',
      )}
    />,
  ];
  const currentUi = '';
  switch (currentUi) {
    case 'OPERATOR': {
      return SHARED_UI_ROUTES;
    }
    case 'PULSE': {
      return SHARED_UI_ROUTES.concat(PULSE_UI_ROUTES);
    }
    default: {
      return SHARED_UI_ROUTES.concat(PULSE_UI_ROUTES).concat(
        <React.Fragment key={'OperationsGroupRoutes'}>
          <ShipmentsRoutes key={'shipmentsRoutes'} />
          <FinanceRoutes key={'FinanceRoutes'} />
          <OperationsDashboardRoutes key={'operationsDashboardRoutes'} />
          <BatchRoutes key={'BatchRoutes'} />
          <DashboardRoutes key={'DashboardRoutes'} />
          <OperationsRoutes key={'OperationsRoutes'} />
          <ReceiveRoutes />
          <SchedulerRoutes />
          <ProducTypesRoutes key={'ProducTypesRoutes'} />
          <ReturnRoutes />
          <ZoningRoutes key={'ZoningRoutes'} />
          <BatchingSettingsRoutes key={'BatchingSettingsRoutes'} />
          <ManifestRoutes key={'ManifestRoutes'} />
          <TripRoutes key={'TripsRoutes'} />
          <VehcileRoutes key="VehcileRoutes" />
          <UsersRoutes />
          <CarriersRoutes key={'CarriersRoutes'} />
          <AccountAdminsRoutes key={'AccountAdminsRoutes'} />
          <FactoryUploadRoutes key={'FactoryUploadRoutes'} />
        </React.Fragment>,
      );
    }
  }
};

const useStyles = makeStyles(
  (theme) => ({
    drawerHeader: {
      display: 'flex',
      alignItems: 'center',
      padding: '0 8px',
      minHeight: 90,
      justifyContent: 'flex-end',
      marginBottom: theme.spacing(4),
    },
    container: {
      paddingLeft: `${theme.spacing(0)}px !important`,
      paddingRight: `${theme.spacing(0)}px !important`,
    },
    root: {
      minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
      maxWidth: 1460,
      width: `calc(100% - 80px)`,
      margin: '0 auto',
    },
    systemMessage: {
      marginTop: 60,
      marginBottom: -50,
      background: '#F3DDDE',
      padding: 15,
      borderRadius: 5,
      border: '1px solid',
      color: '#B65F5E',
      fontWeight: 'bold',
      textTransform: 'uppercase',
    },
  }),
  { name: 'MlcGoMainWrapper' },
);

const MainWrapper = ({ isLogged = false, authorities = [] }) => {
  const classes = useStyles();
  const [systemMessage, setSystemMessage] = useState('');
  const theme = useTheme();

  return (
    <Container maxWidth={theme.layout.width} classes={{ root: classes.container }}>
      <main className={cx(classes.root)}>
        {systemMessage && <div className={classes.systemMessage}>{systemMessage}</div>}
        <div className={classes.drawerHeader} />
        <Switch>{renderRoutes(isLogged, authorities)}</Switch>
      </main>
    </Container>
  );
};

MainWrapper.propTypes = {
  isLogged: PropTypes.bool,
};

export default withData({})(MainWrapper);
